import React from 'react';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';
import { v4 as uuid } from 'uuid';
import { JournalLabel } from 'constants/labels';
import { openModal } from 'actions/modalActions';
import { Grid } from 'components/shared/Grid';
import {
  JOURNAL_REMOVE,
  JOURNAL_POST,
  JOURNAL_UNPOST,
  SET_COLUMN_STATE,
  SET_GROUP_STATE,
} from 'constants/actionTypes';
import {
  journalDetailListingSelector,
  journalEntriesWithItemsSelector,
} from 'selectors/journals';
import { gridStateSelector } from 'selectors/gridState';
import { settingsSelector } from 'selectors/settings';
import { periodIdsSelector } from 'selectors/periods';
import {
  gridComponentDidUpdate,
  handleGridChange,
  handleGridReady,
} from 'utils/grid';
import { currencyValueFormatter, copyCellFormatter } from 'utils/generic';
import {
  currencyComparator,
  dateTimeComparator,
} from 'utils/gridSortingComparators';

import { JOURNALS_GRID } from 'constants/grid';
import { JOURNALS_PATH } from 'constants/paths';

const journalTypeValueFormatter = (value) => value && JournalLabel[value].short;
const journalStatusValueFormatter = (value) =>
  value && value[0].toUpperCase() + value.substr(1);

const getJournalsCols = ({ isDetail = false, settings }) => {
  const cols = [
    {
      width: 100,
      field: 'status',
      colId: 'status',
      valueFormatter: ({ value }) => journalStatusValueFormatter(value),
    },
    {
      field: 'description',
      colId: 'description',
    },
    {
      field: 'periodName',
      colId: 'periodName',
      headerName: 'Period Name',
    },
    {
      field: 'totalDebit',
      colId: 'totalDebit',
      headerName: 'Total Debit',
      type: 'numericColumn',
      valueFormatter: currencyValueFormatter(settings),
      comparator: currencyComparator,
    },
    {
      field: 'totalCredit',
      colId: 'totalCredit',
      headerName: 'Total Credit',
      type: 'numericColumn',
      valueFormatter: currencyValueFormatter(settings),
      comparator: currencyComparator,
    },
    {
      field: 'total',
      colId: 'total',
      headerName: 'Total',
      type: 'numericColumn',
      valueFormatter: currencyValueFormatter(settings),
      comparator: currencyComparator,
    },
    {
      field: 'modified',
      colId: 'modified',
      headerName: 'Modified Date',
      comparator: dateTimeComparator,
    },
  ];

  if (isDetail) {
    const indexOfFirstSumCol = cols.findIndex((c) =>
      c.field.startsWith('total')
    );
    const detailCols = [
      {
        field: 'debit',
        colId: 'debit',
        headerName: 'Debit',
        type: 'numericColumn',
        valueFormatter: currencyValueFormatter(settings),
        comparator: currencyComparator,
      },
      {
        field: 'credit',
        colId: 'credit',
        headerName: 'Credit',
        type: 'numericColumn',
        valueFormatter: currencyValueFormatter(settings),
        comparator: currencyComparator,
      },
    ];

    cols.splice(indexOfFirstSumCol, 0, ...detailCols);
  }

  return cols;
};

class JournalsGrid extends React.PureComponent {
  state = {
    detail: false,
  };

  autoGroupColumnDef = {
    width: 50,
    headerName: 'Type',
    field: 'type',
    colId: 'type',
    valueFormatter: ({ value }) => journalTypeValueFormatter(value),
    cellRendererParams: {
      suppressCount: true,
      suppressDoubleClickExpand: true,
    },
  };

  componentDidUpdate(prevProps) {
    const {
      periodIds,
      colState,
      setColumnState,
      setGroupState,
      path,
    } = this.props;

    const havePeriodsChanged = !isEqual(prevProps.periodIds, periodIds);
    if (havePeriodsChanged) {
      gridComponentDidUpdate(
        colState,
        setColumnState,
        setGroupState,
        path,
        JOURNALS_GRID,
        this.columnApi
      );
    }
  }

  handleGridReady = ({ api, columnApi }) => {
    const { colState, path } = this.props;

    handleGridReady(path, colState, JOURNALS_GRID, api, columnApi);

    this.columnApi = columnApi;
  };

  handleColumnResized = ({ columnApi, finished, source }) => {
    if (source === 'uiColumnDragged' && finished) {
      const { colState, setColumnState, setGroupState, path } = this.props;
      handleGridChange(
        path,
        colState,
        setColumnState,
        setGroupState,
        JOURNALS_GRID,
        columnApi
      );
    }
  };

  handleColumnVisible = ({ columnApi }) => {
    const { colState, setColumnState, setGroupState, path } = this.props;
    handleGridChange(
      path,
      colState,
      setColumnState,
      setGroupState,
      JOURNALS_GRID,
      columnApi
    );
  };

  getNodeId = (d) => `${d.entryId}-${d.id}`;
  getEntryId = (d) => (d.entryId !== undefined ? d.entryId : d.id);

  editEntry = (journalEntryId) => {
    this.props.openModal({
      id: uuid(),
      type: 'component',
      data: { journalEntryId },
      componentType: 'JournalEntryModal',
    });
  };

  handleCellDoubleClick = ({ data }) => {
    const journalEntryId = this.getEntryId(data);
    this.editEntry(journalEntryId);
  };

  processCellForClipboard = (params) => {
    const cellFormatter = copyCellFormatter({ groupsPrecision: -1 });

    return cellFormatter(params);
  };

  getContextMenuItems = ({ api, columnApi, node }) => {
    const { colState, setColumnState, setGroupState, path } = this.props;

    const entryId = node && this.getEntryId(node.data);
    const exportDef = {
      columnGroups: true,
      fileName: `Journals ${this.state.detail ? 'detail ' : ''}listing report`,
      processCellCallback: ({ value, column }) => {
        if (column.colDef.field === 'type') {
          return journalTypeValueFormatter(value);
        }

        if (column.colDef.field === 'status') {
          return journalStatusValueFormatter(value);
        }

        return value;
      },
    };

    return [
      {
        name: 'Edit',
        disabled: !node,
        action: () => this.editEntry(entryId),
      },
      {
        name: 'Remove',
        disabled: !node,
        action: () => this.props.removeEntry(entryId),
      },
      {
        name: 'Copy',
        disabled: !node,
        shortcut: 'Ctrl + C',
        action: () => {
          api.copySelectedRangeToClipboard();
        },
      },
      'separator',
      {
        name: 'Post Journal',
        disabled:
          !node ||
          node.data.total.value !== 0 ||
          (node.data.totalDebit.value === 0 &&
            node.data.totalCredit.value === 0),
        action: () => this.props.postEntry(entryId),
      },
      {
        name: 'Unpost Journal',
        disabled: !node,
        action: () => this.props.unpostEntry(entryId),
      },
      'separator',
      {
        name: 'Detail view',
        checked: this.state.detail,
        action: () => this.setState({ detail: !this.state.detail }),
      },
      ...(this.state.detail ? ['separator', 'expandAll', 'contractAll'] : []),
      'separator',
      {
        name: 'Fit All Columns',
        action: () => {
          api.sizeColumnsToFit();
          handleGridChange(
            path,
            colState,
            setColumnState,
            setGroupState,
            JOURNALS_GRID,
            columnApi
          );
        },
      },
      'separator',
      {
        name: 'Export',
        subMenu: [
          {
            name: 'CSV Export',
            action: () => {
              api.exportDataAsCsv(exportDef);
            },
          },
          {
            name: 'Excel Export',
            action: () => {
              api.exportDataAsExcel({ ...exportDef, exportMode: 'xml' });
            },
          },
        ],
      },
    ];
  };

  render() {
    return (
      <div className="journals-grid">
        <Grid
          columnDefs={getJournalsCols({
            isDetail: this.state.detail,
            settings: this.props.settings,
          })}
          rowData={
            this.state.detail ? this.props.journalsDetail : this.props.journals
          }
          getRowNodeId={this.getNodeId}
          autoGroupColumnDef={this.autoGroupColumnDef}
          treeData={true}
          getDataPath={(data) => data.groupPath}
          onCellDoubleClicked={this.handleCellDoubleClick}
          enableRangeSelection={true}
          processCellForClipboard={this.processCellForClipboard}
          getContextMenuItems={this.getContextMenuItems}
          onGridReady={this.handleGridReady}
          onColumnVisible={this.handleColumnVisible}
          onColumnResized={this.handleColumnResized}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  periodIds: periodIdsSelector(state),
  journals: journalEntriesWithItemsSelector(state),
  journalsDetail: journalDetailListingSelector(state),
  colState: gridStateSelector(state),
  settings: settingsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  openModal: (modalConfig) => dispatch(openModal(modalConfig)),
  removeEntry: (entryId) => dispatch({ type: JOURNAL_REMOVE, entryId }),
  postEntry: (entryId) => dispatch({ type: JOURNAL_POST, entryId }),
  unpostEntry: (entryId) => dispatch({ type: JOURNAL_UNPOST, entryId }),
  setColumnState: (path, columnState) =>
    dispatch({
      type: SET_COLUMN_STATE,
      path,
      grid: JOURNALS_GRID,
      columnState,
    }),
  setGroupState: (path, groupState) =>
    dispatch({
      type: SET_GROUP_STATE,
      path,
      grid: JOURNALS_GRID,
      groupState,
    }),
});

JournalsGrid.defaultProps = {
  path: JOURNALS_PATH,
};

export default connect(mapStateToProps, mapDispatchToProps)(JournalsGrid);
